
import DeleteModal from '@/components/modals/DeleteModal.vue'
import { Component, Mixins, Prop, Ref } from 'vue-property-decorator'
import ToastMixin from '@/mixins/ToastMixin'
import MobileMixin from '@/mixins/MobileMixin'
import BreadcrumbHeader from '@/components/processing_steps/BreadcrumbHeader.vue'
import LocaleMixin from '@/mixins/LocaleMixin'
import { BvTableFieldArrayWithStickColumn, IBreadcrumb, TGenericObject } from '@/types/base'
import { BvTableCtxObject } from "bootstrap-vue"
import { IFilter, IFilters } from "@/types/filters"
import { IShowcasePageTableItem } from "@/types/showcase"
import { API_URLS, filtersToArray, SITE_URLS } from "@/utils/helpers"
import GenericTable from "@/components/GenericTable.vue"
import GenericTableMixin from "@/mixins/GenericTableMixin"
import axios from 'axios'
import ShowcaseCategoryMixin from './ShowcaseCategoryMixin.vue'
import ProjectLinkButton from '../buttons/ProjectLinkButton.vue'
import PreviewButton from '../buttons/PreviewButton.vue'
import EditButton from '@/components/buttons/EditButton.vue'
import DeleteButton from '../buttons/DeleteButton.vue'
import QrCodeButton from '../buttons/QrCodeButton.vue'
import CopyURLButton from "@/components/buttons/CopyURLButton.vue"
import DuplicateButton from "@/components/buttons/DuplicateButton.vue"
import IconButton from '../buttons/IconButton.vue'
import ConfirmModal from '../processing_steps/commons/modals/ConfirmModal.vue'
import FormTitle from '@/components/processing_steps/FormTitle.vue'
import AccountProjectList from '@/views/AccountProjectList.vue'
import StatusFormatterMixin from '@/mixins/StatusFormatterMixin'
import OrgaHasNoTemplateBanner from "@/components/processing_steps/banners/OrgaHasNoTemplateBanner.vue"

@Component({
  components: {
    OrgaHasNoTemplateBanner,
    CopyURLButton,
    GenericTable,
    BreadcrumbHeader,
    DeleteModal,
    ProjectLinkButton,
    PreviewButton,
    EditButton,
    DeleteButton,
    QrCodeButton,
    DuplicateButton,
    IconButton,
    ConfirmModal,
    FormTitle,
    AccountProjectList
  },
  mixins: [MobileMixin, GenericTableMixin, ShowcaseCategoryMixin]
})
export default class ShowcasePageTable extends Mixins(LocaleMixin, ToastMixin, ShowcaseCategoryMixin, StatusFormatterMixin) {
  @Ref() readonly selectableTable!: GenericTable
  @Prop({ default: null }) defaultFilters?: IFilters
  @Prop({ default: 1 }) page!: number
  @Prop({ default: 25 }) pageSize!: number

  busy = false
  selectedShowcasePage: IShowcasePageTableItem = null
  showcasePages: IShowcasePageTableItem[] = []
  deleteModalOpen = false
  tableName = "showcase_page_table"
  currentPage = 1
  tableLoading = true
  sortDesc = false
  filtersChanged = false
  sendMailModalOpen = false
  duplicateShowcaseModalOpen = false
  finishModalOpen = false
  pauseModalOpen = false
  resumeModalOpen = false
  noTemplateAvailable = false
  searchString = ""
  sortBy = ""
  tableContext: BvTableCtxObject | null = null
  fields: BvTableFieldArrayWithStickColumn = [
    { key: 'title', label: this.$gettext('Page title'), sortable: true },
    { key: 'status', label: 'Status', sortable: false, stickyColumn: true },
    { key: 'created_at', label: this.$gettext('Created at'), sortable: true, formatter: this.formatDate, dateRangeFilter: true },
    { key: 'view_actions', label: this.$gettext('Funding page links') },
    { key: 'fixed_action_col', label: this.$gettext('Actions') },
    { key: 'status',
      label: this.$gettext('Is public'),
      sortable: true,
      formatter: (status: number): string => {
        return status === 2 ? this.$gettext('Yes') : this.$gettext('No')
      }
    }
  ]

  customFields = ["status", "view_actions"]

  getShowButton(status: number, buttonType: 'preview' | 'edit' | 'pause' |'resume' |
    'widget' | 'qr' | 'email' | 'finish' | 'delete'
  ): boolean {
    /*
      Draft = ChoiceItem(1, _("in Progress"))
      Public = ChoiceItem(2, _("complete"))
      Archived = ChoiceItem(3, _("archived"))
      Paused = ChoiceItem(4, _("Paused"))
    */
    if (buttonType === 'preview') {
      return status === 1
    } else if (buttonType === 'edit') {
      return true
    } else if (buttonType === 'pause') {
      return status !== 4
    } else if (buttonType === 'resume') {
      return status === 4
    } else if (buttonType === 'widget') {
      return status === 2
    } else if (buttonType === 'qr') {
      return status === 2
    } else if (buttonType === 'email') {
      return status === 2
    } else if (buttonType === 'finish') {
      return status !== 3 && status !== 1
    } else if (buttonType === 'delete') {
      return status === 1
    }
    return false
  }

  get apiUrl (): string {
    return API_URLS.SHOWCASE_PAGE.LIST
  }

  getCopyText (slug: string): string {
    return window.location.origin + '/showcase/' + slug + '/widget/'
  }

  get apiUrlExtraParams (): TGenericObject {
    return {
      category: this.categoryKey
    }
  }

  get isSiteAdmin () {
    return this.$route.name.startsWith("site-admin")
  }

  get editViewUrl (): string {
    return this.isSiteAdmin ? 'site-admin-showcase-update' : 'account-showcase-update'
  }

  get createViewUrl (): string {
    const route = this.isSiteAdmin ? { name: 'site-admin-showcase-create' } : { name: 'account-showcase-create' }
    return this.$router.resolve(route).href
  }

  get breadcrumbs (): IBreadcrumb[] {
    return [
      {
        title: this.$gettext('Showcase pages'),
        url: '#'
      },
      {
        title: this.categoryDisplay,
        url: '#'
      }
    ]
  }

  getEditHref (slug: string): string {
    if (this.isSiteAdmin) {
      return SITE_URLS.SHOWCASE.SITE_ADMIN.UPDATE(slug, this.categoryKey)
    } else {
      return SITE_URLS.SHOWCASE.ACCOUNT.UPDATE(slug, this.categoryKey)
    }
  }

  publicUrl (showcasePage: IShowcasePageTableItem): string {
    return window.location.protocol + '//' + window.location.host + SITE_URLS.SHOWCASE.PUBLIC(showcasePage.slug)
  }

  openDeleteModal (showcasePage: IShowcasePageTableItem): void {
    this.selectedShowcasePage = showcasePage
    this.deleteModalOpen = true
  }

  confirmDelete (): void {
    axios.delete(API_URLS.SHOWCASE_PAGE.RETRIEVE(this.selectedShowcasePage.slug)).then(() => {
      this.selectedShowcasePage = null
      this.selectableTable.loadItems()
    })
  }

  async copyWidgetUrlToClipboard (slug: string) {
    await navigator.clipboard.writeText(window.location.origin + '/showcase/' + slug + '/widget/')
    this.makeToast(
      'success',
      this.$gettext('Success'),
      this.$gettext('Successfully copied widget url to clipboard.')
    )
  }

  onRowSelected (selectedItems: IShowcasePageTableItem): void {
    this.selectedShowcasePage = selectedItems
  }

  get activeFilters (): IFilter[] {
    return filtersToArray(this.defaultFilters)
  }

  sortingChanged (ctx: BvTableCtxObject): void {
    this.tableContext = ctx
  }

  copiedWidgetUrl () {
    this.makeToast(
      'success',
      this.$gettext('Success'),
      this.$gettext('Successfully copied widget url to clipboard.')
    )
  }

  reloadTable () {
    this.selectableTable.loadItems()
  }

  openSendMailModal (showcasePage: IShowcasePageTableItem): void {
    this.selectedShowcasePage = showcasePage
    this.sendMailModalOpen = true
  }

  async sendProjectViaEmail (): Promise<void> {
    if (!this.selectedShowcasePage) return
    await axios.post(API_URLS.SHOWCASE_PAGE.SEND_VIA_EMAIL(this.selectedShowcasePage.slug))
      .then(() => {
        this.makeToast(
          'success',
          this.$gettext('Success'),
          this.$gettext('We have sent you an email with your project.')
        )
        this.sendMailModalOpen = false
      })
      .catch(() => {
        this.makeToast(
          'danger',
          this.$gettext('Error'),
          this.$gettext('The project cannot be sent via email.')
        )
        this.sendMailModalOpen = false
      })
    this.selectedShowcasePage = null
  }

  async duplicateShowcase (): Promise<void> {
    this.busy = true
    this.makeToast(
      'info',
      this.$gettext('Duplicating page'),
      this.$gettext('Please wait...')
    )
    const url = API_URLS.SHOWCASE_PAGE.DUPLICATE(this.selectedShowcasePage.slug)
    axios.post(url).then(() => {
      this.busy = false
      this.makeToast(
        'success',
        this.$gettext('Success'),
        this.$gettext('Page successfully duplicated.')
      )
      this.reloadTable()
    }).catch(() => {
      this.busy = false
      this.makeToast(
        'error',
        this.$gettext('Error'),
        this.$gettext('Failed to duplicate page.')
      )
    })
  }

  openPauseProjectModal (showcase: IShowcasePageTableItem) {
    this.selectedShowcasePage = showcase
    this.pauseModalOpen = true
  }

  openResumeProjectModal (showcase: IShowcasePageTableItem) {
    this.selectedShowcasePage = showcase
    this.resumeModalOpen = true
  }

  openFinishProjectModal (showcase: IShowcasePageTableItem) {
    this.selectedShowcasePage = showcase
    this.finishModalOpen = true
  }

  openDuplicateModal (showcase: IShowcasePageTableItem) {
    this.selectedShowcasePage = showcase
    this.duplicateShowcaseModalOpen = true
  }

  async changeStatus (status: 'resume' | 'pause' | 'finish'): Promise<void> {
    if (!this.selectedShowcasePage) return
    const postData = {
      status: status === 'finish' ? 3 : status === 'pause' ? 4 : 2
    }
    await axios.put(API_URLS.SHOWCASE_PAGE.UPDATE_COMPLETITION(this.selectedShowcasePage.slug), postData).then(async () => {
      this.selectableTable.loadItems()
    })
  }
}
